
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  RefreshCw, 
  Trash2, 
  Clock, 
  TestTube, 
  Key, 
  ExternalLink, 
  Globe, 
  Zap,
  Download
} from "lucide-react";
import { useAuthVerificationContext } from "@/contexts/AuthVerificationContext";
import { useToast } from "@/hooks/use-toast";
import { authNavigationController } from "@/controllers/AuthNavigationController";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface DebugEntry {
  timestamp: string;
  message: string;
  type: "info" | "success" | "error" | "warning";
  details?: any;
}

export const DebugConsole: React.FC = () => {
  const [logs, setLogs] = useState<DebugEntry[]>([]);
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingLive, setIsGeneratingLive] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [flowState, setFlowState] = useState(null);
  const { 
    debugLogs, 
    clearDebugLogs, 
    addDebugLog, 
    createTestAuthFlow,
    createLiveAuthFlow
  } = useAuthVerificationContext();
  const { toast } = useToast();
  
  useEffect(() => {
    if (debugLogs) {
      setLogs(debugLogs);
    }
    
    const checkFlowState = () => {
      const state = authNavigationController?.getState();
      setFlowState(state);
    };
    
    checkFlowState();
    const interval = setInterval(checkFlowState, 3000);
    
    return () => clearInterval(interval);
  }, [debugLogs]);
  
  const handleClearLogs = () => {
    clearDebugLogs();
    setLogs([]);
    setVerificationUrl(null);
  };
  
  const handleAddTestLog = () => {
    const types: Array<"info" | "success" | "error" | "warning"> = ["info", "success", "error", "warning"];
    const randomType = types[Math.floor(Math.random() * types.length)];
    
    const sampleDetails = {
      userId: "test-user-123",
      operation: "auth-verification-test",
      metadata: {
        source: "test-button",
        timestamp: new Date().toISOString(),
        version: "1.0",
        role: "auto_mock"
      }
    };
    
    addDebugLog(
      `Test log entry created manually: ${new Date().toLocaleTimeString()}`, 
      randomType, 
      sampleDetails
    );
  };
  
  const handleTestMockAuthFlow = async () => {
    try {
      setIsGenerating(true);
      setVerificationUrl(null);
      
      addDebugLog("Starting test mock auth flow...", "info", { role: "auto_mock" });
      
      const url = await createTestAuthFlow();
      
      if (url) {
        setVerificationUrl(url);
        toast({
          title: "Test Mock Auth Flow Ready",
          description: "Mock verification URL has been generated with auto_mock role. Click the link to complete the auth flow in a new tab.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Test Mock Auth Flow Failed",
          description: "Failed to generate mock verification URL. Check debug logs for details.",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An unexpected error occurred",
      });
      addDebugLog(`Error in test mock auth flow: ${error.message}`, "error", error);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleTestLiveAuthFlow = async () => {
    try {
      setIsGeneratingLive(true);
      setVerificationUrl(null);
      
      addDebugLog("Starting live auth flow with real email...", "info", {
        status: "starting",
        message: "Creating a user with auto_live role and sending a real verification email",
        role: "auto_live"
      });
      
      const result = await createLiveAuthFlow();
      
      if (result) {
        toast({
          title: "Live Email Sent",
          description: "A real verification email has been sent to 20@lamulamu.com. Check your inbox to complete the verification flow.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Live Auth Flow Failed",
          description: "Failed to generate real verification email. Check debug logs for details.",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An unexpected error occurred",
      });
      addDebugLog(`Error in live auth flow: ${error.message}`, "error", error);
    } finally {
      setIsGeneratingLive(false);
    }
  };
  
  const exportLogs = () => {
    try {
      const data = logs;
      const filename = `debug-logs-${new Date().toISOString()}.json`;
      const contentType = "application/json";
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: contentType });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.download = filename;
      link.href = url;
      link.click();
      
      URL.revokeObjectURL(url);
      
      toast({
        title: "Export complete",
        description: "Debug logs exported as JSON"
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Export failed",
        description: error.message || "Failed to export logs"
      });
    }
  };

  const getLogTypeColor = (type: string) => {
    switch (type) {
      case "success":
        return "bg-green-500 text-white";
      case "error":
        return "bg-red-500 text-white";
      case "warning":
        return "bg-yellow-500 text-white";
      default:
        return "bg-blue-500 text-white";
    }
  };
  
  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Debug Console</CardTitle>
              <CardDescription>Real-time debugging for authentication verification</CardDescription>
            </div>
            <div className="flex gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={toggleAutoRefresh}
                    >
                      {autoRefresh ? (
                        <RefreshCw className="h-4 w-4 text-green-500" />
                      ) : (
                        <RefreshCw className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{autoRefresh ? "Disable" : "Enable"} auto-refresh (5s)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="sm" onClick={exportLogs}>
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export logs</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-start">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    onClick={handleTestMockAuthFlow} 
                    variant="outline" 
                    size="sm"
                    disabled={isGenerating}
                  >
                    <Key className="mr-2 h-4 w-4" />
                    {isGenerating ? "Generating..." : "Test Mock URL"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Generate a mock verification URL with auto_mock role that works locally without calling edge functions</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    onClick={handleTestLiveAuthFlow} 
                    variant="outline" 
                    size="sm"
                    disabled={isGeneratingLive}
                  >
                    <Globe className="mr-2 h-4 w-4" />
                    {isGeneratingLive ? "Sending email..." : "Test Live Email"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Create a user with auto_live role and send a real verification email to 20@lamulamu.com using Supabase's email service</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button 
              onClick={handleAddTestLog} 
              variant="outline" 
              size="sm"
            >
              <TestTube className="mr-2 h-4 w-4" />
              Test Log
            </Button>
            <Button 
              onClick={handleClearLogs} 
              variant="outline" 
              size="sm"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Clear Logs
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {flowState && flowState.type && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="font-medium mb-2">Auth Flow State</h3>
            <div className="text-sm space-y-1">
              <p><span className="font-semibold">Type:</span> {flowState.type}</p>
              <p><span className="font-semibold">Stage:</span> {flowState.stage}</p>
              {flowState.email && <p><span className="font-semibold">Email:</span> {flowState.email}</p>}
              {flowState.userId && <p><span className="font-semibold">User ID:</span> {flowState.userId}</p>}
              <p><span className="font-semibold">Last Update:</span> {new Date(flowState.timestamp).toLocaleTimeString()}</p>
            </div>
            <Button 
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => {
                authNavigationController.clearState();
                setFlowState(null);
                toast({
                  title: "State cleared",
                  description: "Auth flow state has been cleared"
                });
              }}
            >
              Clear Flow State
            </Button>
          </div>
        )}
        
        {verificationUrl && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <h3 className="font-medium mb-2">Verification URL Generated</h3>
            <p className="text-sm mb-2">Click the link below to complete the auth flow in a new tab:</p>
            <div className="flex items-center">
              <a 
                href={verificationUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline flex items-center text-sm"
              >
                {verificationUrl.substring(0, 50)}...
                <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </div>
            <p className="text-xs mt-2 text-gray-500">Note: This will create a real user in the database and complete the actual authentication flow.</p>
          </div>
        )}
        
        {logs.length > 0 ? (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {logs.map((log, index) => (
                <div 
                  key={index} 
                  className="border-l-4 pl-3 py-2 border-l-blue-500"
                  style={{ borderLeftColor: log.type === "success" ? "#10b981" : log.type === "error" ? "#ef4444" : log.type === "warning" ? "#f59e0b" : "#3b82f6" }}
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <Badge className={getLogTypeColor(log.type)}>
                      {log.type.charAt(0).toUpperCase() + log.type.slice(1)}
                    </Badge>
                  </div>
                  <div className="text-sm">{log.message}</div>
                  {log.details && (
                    <div className="mt-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      {typeof log.details === 'object' ? (
                        <pre className="whitespace-pre-wrap break-words max-w-full">{JSON.stringify(log.details, null, 2)}</pre>
                      ) : (
                        log.details
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex flex-col items-center justify-center h-[200px] text-gray-500">
            <p>No debug logs available</p>
            <p className="text-sm mt-2">Complete an authentication flow to see logs here</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
